import React, { useState } from 'react';
import { useUa } from './UserAdminContext';
import * as api from './userAdminApi';
import { Modal } from './UaOverlay';
import { DeptPicker, Field } from './UaBits';
import {
    EMPTY_FILTERS, dayMs, deptList, fmtDate, fullName, inviteMessage, isoIn, personById, plural, roleById, roleName, suggestedRole,
} from './uaModel';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 8;

/**
 * Add user, or (with `setup`) give someone who already signs in to Bridge departments and a role.
 * The first department with a usual role suggests it.
 */
export function AddUserDialog({ setup }) {
    const { catalog, data, run, toast, actions, closeModal, setFilters, setSel, setFlash } = useUa();
    // Setting someone up starts from the user table: their departments, the suggested role, and their job title.
    const [f, setF] = useState(() => {
        const start = setup ? suggestedRole(catalog, setup.dept) : null;
        return {
            first: setup ? setup.first : '', last: setup ? setup.last : '', email: setup ? setup.email : '',
            title: setup ? setup.title || '' : '', dept: setup ? setup.dept || '' : '',
            role: start ? start.role : '', password: '',
        };
    });
    const [roleAuto, setRoleAuto] = useState(() => !!(setup && suggestedRole(catalog, setup.dept)));
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const suggestion = suggestedRole(catalog, f.dept);
    const role = roleById(data, f.role);

    const clearErrors = (...keys) => setErrors((old) => { const next = { ...old }; keys.forEach((k) => delete next[k]); return next; });
    const set = (k, v) => { setF((old) => ({ ...old, [k]: v })); clearErrors(k); };
    // While the role is still the suggested one, it follows the departments.
    const setDept = (dept) => {
        const auto = !f.role || roleAuto;
        const next = suggestedRole(catalog, dept);
        setF((old) => ({ ...old, dept, role: auto ? (next ? next.role : '') : old.role }));
        if (auto) setRoleAuto(!!next);
        clearErrors('dept', 'role');
    };
    const setRole = (r) => { set('role', r); setRoleAuto(false); };

    // Show the new row: clear the filters and highlight it for a moment.
    const showRow = (id) => {
        setFilters((old) => ({ ...EMPTY_FILTERS, sort: old.sort, dir: old.dir }));
        setSel(new Set());
        if (id) setFlash(id);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        const email = f.email.trim().toLowerCase();
        const err = {};
        if (!f.first.trim()) err.first = 'Enter a first name.';
        if (!f.last.trim()) err.last = 'Enter a last name.';
        if (!setup) {
            if (!EMAIL_RE.test(email)) err.email = 'Enter a valid work email.';
            else if (data.people.some((p) => p.email === email)) err.email = 'Someone already uses this email.';
            if (f.password.length < MIN_PASSWORD) err.password = `Use at least ${MIN_PASSWORD} characters.`;
        }
        if (!deptList(f.dept).length) err.dept = 'Pick at least one department.';
        if (!f.role) err.role = 'Pick a role.';
        setErrors(err);
        if (Object.keys(err).length) return;

        setBusy(true);
        const name = <b>{`${f.first.trim()} ${f.last.trim()}`}</b>;
        if (setup) {
            const res = await run(
                api.importPeople([{ email: setup.email, first: f.first.trim(), last: f.last.trim(), title: f.title.trim() }], f.dept, f.role),
                <>{name} is set up as {roleName(data, f.role)}.</>, { undo: false },
            );
            setBusy(false);
            if (res) { closeModal(); showRow(res.result && res.result.created[0]); }
            return;
        }
        const { password } = f;
        const res = await run(api.addPerson({ first: f.first, last: f.last, email, title: f.title, dept: f.dept, role: f.role, password }), null);
        setBusy(false);
        if (!res) return;
        closeModal();
        showRow(res.result.id);
        toast(<>{name} added and can sign in. Send them the invite with their temporary password.</>, {
            action: { label: 'Copy invite', onClick: () => actions.shareInvite(res.result, password) },
        });
    };

    const input = (key, label, opts = {}) => (
        <Field id={`ua-a-${key}`} label={label} error={errors[key]} full={opts.full}>
            <input
                id={`ua-a-${key}`} className="ua-inp" type={opts.type || 'text'} value={f[key]} placeholder={opts.placeholder}
                disabled={opts.disabled} autoComplete="off" maxLength={opts.max || 60} onChange={(e) => set(key, e.target.value)}
            />
        </Field>
    );

    return (
        <Modal
            wide onClose={closeModal}
            title={setup ? `Set up ${fullName(setup)}` : 'Add user'}
            text={setup
                ? 'They already sign in to Bridge. Their departments and job title come from the user table, and the role is the one their first department usually gets. Check them, then set them up.'
                : 'They show as Invited until they first sign in.'}
            footer={(
                <>
                    <button type="button" className="ua-btn" onClick={closeModal}>Cancel</button>
                    <button type="submit" form="ua-add-form" className="ua-btn ua-btn-primary" disabled={busy}>{setup ? 'Set up' : 'Add user'}</button>
                </>
            )}
        >
            <form id="ua-add-form" className="ua-grid2" onSubmit={submit} noValidate>
                {input('first', 'First name')}
                {input('last', 'Last name')}
                {input('email', 'Work email', { type: 'email', placeholder: 'name@gigaflightinc.com', full: true, disabled: !!setup, max: 120 })}
                {setup ? null : (
                    <Field
                        id="ua-a-password" label="Temporary password" full error={errors.password}
                        help="Creates their sign-in account. You’ll copy it into the invite; Bridge doesn’t keep it."
                    >
                        <input
                            id="ua-a-password" className="ua-inp" type="text" value={f.password} autoComplete="new-password"
                            spellCheck={false} maxLength={64} onChange={(e) => set('password', e.target.value)}
                        />
                    </Field>
                )}
                {input('title', 'Job title', { placeholder: 'e.g. Senior Buyer', max: 80, full: true })}
                <DeptPicker
                    id="ua-a-dept" label="Departments" departments={catalog.departments} value={f.dept} onChange={setDept}
                    error={errors.dept} help="Pick every department they work in."
                />
                <Field
                    id="ua-a-role" label="Role" full error={errors.role}
                    help={role ? `${role.desc} You can change single pages later.` : 'The role decides which pages they can open.'}
                >
                    <select id="ua-a-role" className="ua-sel" value={f.role} onChange={(e) => setRole(e.target.value)}>
                        <option value="">Choose…</option>
                        {data.roles.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}{suggestion && suggestion.role === r.id ? ` (suggested for ${suggestion.dept})` : ''}</option>
                        ))}
                    </select>
                </Field>
            </form>
        </Modal>
    );
}

/** Deactivate one or several users, right away or on their last day. */
export function DeactivateDialog({ ids }) {
    const { catalog, data, run, closeModal, setSel } = useUa();
    const people = ids.map((id) => personById(data, id)).filter(Boolean);
    const one = people.length === 1 ? people[0] : null;
    const [when, setWhen] = useState(one && one.scheduledOff ? 'date' : 'now');
    const [date, setDate] = useState(one && one.scheduledOff ? one.scheduledOff : isoIn(7));
    const [reason, setReason] = useState((one && one.schedReason) || catalog.reasons[0]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    if (!people.length) return null;
    const who = one ? <b>{fullName(one)}</b> : plural(people.length, 'user');

    const submit = async () => {
        if (when === 'date' && (!date || date < isoIn(1))) { setError('Pick a date after today.'); return; }
        setBusy(true);
        const res = await run(
            api.deactivate(people.map((p) => p.id), { when, date: when === 'date' ? date : undefined, reason }),
            when === 'date' ? <>{who} keeps access until the end of {fmtDate(dayMs(date))}.</> : <>{who} {one ? 'is' : 'are'} deactivated.</>,
        );
        setBusy(false);
        if (res) { closeModal(); setSel(new Set()); }
    };

    const cancelLastDay = async () => {
        setBusy(true);
        const res = await run(api.cancelDeactivation(one.id), <>{who} keeps their access.</>);
        setBusy(false);
        if (res) closeModal();
    };

    return (
        <Modal
            onClose={closeModal}
            title={one ? `Deactivate ${fullName(one)}?` : `Deactivate ${people.length} users?`}
            text="They won’t be able to sign in to Bridge. Their orders, comments and history stay as they are, and you can reactivate them later."
            footer={(
                <>
                    {one && one.scheduledOff ? (
                        <button type="button" className="ua-btn ua-btn-ghost ua-mr-auto" onClick={cancelLastDay} disabled={busy}>Cancel last day</button>
                    ) : null}
                    <button type="button" className="ua-btn" onClick={closeModal}>Cancel</button>
                    <button type="button" className="ua-btn ua-btn-danger" onClick={submit} disabled={busy}>{when === 'date' ? 'Set last day' : 'Deactivate'}</button>
                </>
            )}
        >
            <div className="ua-f">
                <span className="ua-lbl" id="ua-when-lbl">When</span>
                <div className="ua-radios" role="radiogroup" aria-labelledby="ua-when-lbl">
                    <label><input type="radio" name="ua-when" value="now" checked={when === 'now'} onChange={() => { setWhen('now'); setError(''); }} />Right away</label>
                    <label><input type="radio" name="ua-when" value="date" checked={when === 'date'} onChange={() => setWhen('date')} />On their last day</label>
                </div>
                {when === 'date' ? (
                    <>
                        <input
                            className="ua-inp ua-date" type="date" min={isoIn(1)} value={date} aria-label="Last day"
                            onChange={(e) => { setDate(e.target.value); setError(''); }}
                        />
                        {error ? <span className="ua-emsg">{error}</span> : <span className="ua-help">Access ends at the end of that day.</span>}
                    </>
                ) : null}
            </div>
            <Field id="ua-reason" label="Reason">
                <select id="ua-reason" className="ua-sel" value={reason} onChange={(e) => setReason(e.target.value)}>
                    {catalog.reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
            </Field>
        </Modal>
    );
}

export function ReactivateDialog({ id }) {
    const { data, run, closeModal } = useUa();
    const person = personById(data, id);
    const [role, setRole] = useState(person ? person.role : '');
    const [busy, setBusy] = useState(false);
    if (!person) return null;

    const submit = async () => {
        setBusy(true);
        const res = await run(api.reactivate(person.id, role), <><b>{fullName(person)}</b> can sign in again.</>);
        setBusy(false);
        if (res) closeModal();
    };

    return (
        <Modal
            onClose={closeModal} title={`Reactivate ${fullName(person)}?`} text="They can sign in again with this role."
            footer={(
                <>
                    <button type="button" className="ua-btn" onClick={closeModal}>Cancel</button>
                    <button type="button" className="ua-btn ua-btn-primary" onClick={submit} disabled={busy}>Reactivate</button>
                </>
            )}
        >
            <Field id="ua-react-role" label="Role">
                <select id="ua-react-role" className="ua-sel" value={role} onChange={(e) => setRole(e.target.value)}>
                    {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
            </Field>
        </Modal>
    );
}

/** Shown when the browser won't let the page copy the invite: the text is selected to copy by hand. */
export function InviteDialog({ id, password }) {
    const { data, run, closeModal } = useUa();
    const person = personById(data, id);
    if (!person) return null;
    const markSent = async () => {
        const res = await run(api.markInvited(person.id), <>Noted that you sent the invite to <b>{fullName(person)}</b>.</>, { undo: false });
        if (res) closeModal();
    };
    return (
        <Modal
            onClose={closeModal} title={`Invite for ${fullName(person)}`}
            text="Your browser didn’t let the page copy it. The text below is selected: press Ctrl+C, then paste it into an email or Teams chat."
            footer={(
                <>
                    <button type="button" className="ua-btn" onClick={closeModal}>Close</button>
                    <button type="button" className="ua-btn ua-btn-primary" onClick={markSent}>I’ve sent it</button>
                </>
            )}
        >
            <label className="ua-sr" htmlFor="ua-invite-text">Invite message</label>
            <textarea id="ua-invite-text" data-autofocus className="ua-inp ua-textarea" readOnly rows={7} value={inviteMessage(person, password)} onFocus={(e) => e.target.select()} />
        </Modal>
    );
}
